import { useState, useRef } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { Camera, FileText } from 'lucide-react'
import { api } from '../../lib/api'
import { micsoreazaPoza } from '../../lib/micsoreazaPoza'
import { Card } from '../../components/ui/Card'
import { Button } from '../../components/ui/Button'

type FileStatus =
  | 'done'
  | 'processing'
  | 'failed'
  | 'pending_scan'
  | 'skipped'
  | 'deleted'
  | 'mutat'

interface RandCitit {
  lineId: number
  /** Vine din e-factură (adăugat cu „Adaugă factura din SPV”): nu e citit, nu se corectează. */
  dinSpv?: boolean
  furnizor: string | null
  total: number
  data: string | null
  /** O poziție copiată de pe bon, sau o încadrare când sunt mai multe. */
  articole: string | null
  /** Împărțirea pe cote de TVA, când bonul are două — mâncare la 11%, băutură la 21%. */
  cote: { cota: number; baza: number; tva: number }[]
  /** Ce anume nu se leagă și se vede pe bon (suma, data). Gol când citirea e în regulă. */
  deVerificat?: string[]
  /** Ai confirmat sau ai corectat rândul. */
  confirmat?: boolean
}

interface Eveniment {
  id: number
  cod: string
  titlu: string
  /** Acțiunile de teren ale evenimentului. Goale la majoritatea — atunci nu apare niciun câmp. */
  actiuni?: { id: number; denumire: string }[]
  /** `15 oct – 18 oct 2026`. Titlurile se repetă — „Medic.ro" e și la Timișoara, și la Craiova. */
  perioada: string
}

interface Confirmare {
  decontId: number
  period: string
  faraDescriere: { fileId: number; fileName: string }[]
  totalBonuri: number
  confirmatLa: string | null
  confirmatDe: string | null
  inchisPentruAngajat: boolean
  /** Bonuri (+ diurnă, km) și facturile din SPV, separat; totalul le cumulează. */
  valoare?: { cheltuieli: number; facturi: number; total: number; moneda: string }
}

interface UploadedFile {
  name: string
  fileId: number | null
  /** Ce reprezintă cheltuiala, în cuvintele angajatului. Fără ea nu se poate confirma. */
  descriere: string | null
  /** Ce a citit sistemul din poză, pe rânduri. */
  citit?: RandCitit[]
  /** Contabilitatea cere o poză mai bună, și de ce. */
  refacereMotiv?: string | null
  campanieId?: number | null
  actiuneId?: number | null
  campanieTitlu?: string | null
  sizeKb: number
  uploadedAt: string | null
  status: FileStatus
  lineCount: number
  deleted: boolean
  /** De ce a ajuns bonul pe alta luna decat cea pe care a fost incarcat. */
  nota?: string | null
}

interface UploadInfo {
  linked: boolean
  employeeName?: string
  months?: string[]
  month?: string
  uploaded?: UploadedFile[]
  confirmare?: Confirmare | null
}

/** `1.234,50 lei` — moneda decontului, `LEI` scrisă ca pe restul ecranului. */
function fmtSuma(n: number, moneda: string): string {
  return `${n.toLocaleString('ro-RO', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${
    moneda === 'LEI' ? 'lei' : moneda
  }`
}

/** Eticheta + culoare pentru statusul de procesare al unui bon. */
function statusBadge(f: UploadedFile): { label: string; cls: string } {
  switch (f.status) {
    case 'done':
      return {
        label: `✓ Procesat${f.lineCount > 0 ? ` · ${f.lineCount} rânduri` : ''}`,
        cls: 'bg-green-100 text-green-700',
      }
    case 'processing':
      return { label: '⏳ În procesare', cls: 'bg-blue-100 text-blue-700' }
    case 'failed':
      return {
        label: '⚠ Nu s-a putut citi — contabilitatea îl completează manual',
        cls: 'bg-red-100 text-red-700',
      }
    case 'pending_scan':
      return { label: '⏳ În așteptare', cls: 'bg-b4l-bg text-b4l-text-muted' }
    case 'deleted':
      return { label: '🗑 Șters', cls: 'bg-b4l-bg text-b4l-text-muted' }
    case 'mutat':
      return { label: '→ Mutat pe luna lui', cls: 'bg-b4l-amber-light text-b4l-amber-dark' }
    default:
      return { label: 'Document', cls: 'bg-b4l-bg text-b4l-text-muted' }
  }
}

/** Data scurta din timestamp ISO (sau din numele fisierului bon-YYYY-MM-DD...). */
function shortDate(f: UploadedFile): string {
  if (f.uploadedAt) {
    const d = new Date(f.uploadedAt)
    if (!Number.isNaN(d.getTime()))
      return d.toLocaleString('ro-RO', { dateStyle: 'short', timeStyle: 'short' })
  }
  const m = f.name.match(/(\d{4})-(\d{2})-(\d{2})T(\d{2})-(\d{2})/)
  if (m) return `${m[3]}.${m[2]}.${m[1]} ${m[4]}:${m[5]}`
  return f.name
}

export function IncarcaBonPage() {
  const queryClient = useQueryClient()
  const [month, setMonth] = useState<string | null>(null)
  const [viewFile, setViewFile] = useState<UploadedFile | null>(null)
  const [facturaSpv, setFacturaSpv] = useState(false)
  const fileRef = useRef<HTMLInputElement>(null)

  const query = useQuery<UploadInfo>({
    queryKey: ['upload-me', month],
    queryFn: async () => {
      const q = month ? `?month=${encodeURIComponent(month)}` : ''
      return (await api.get<UploadInfo>(`/upload/me${q}`)).data
    },
    // Cat timp exista bonuri in procesare, reincarca periodic pana se finalizeaza.
    refetchInterval: (q) =>
      (q.state.data?.uploaded ?? []).some(
        (f) => f.status === 'processing' || f.status === 'pending_scan',
      )
        ? 5000
        : false,
  })

  const info = query.data
  const activeMonth = month ?? info?.month ?? ''

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['upload-me'] })

  function errMsg(err: unknown, fallback: string): string {
    return (
      (err as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error
        ?.message ?? fallback
    )
  }

  const uploadMutation = useMutation({
    mutationFn: async (file: File) => {
      /*
        Pozele se micsoreaza inainte de urcare; PDF-urile NU se ating.

        O poza de telefon are 3-8 MB, iar la stand se urca de pe date mobile. Nu se pierde nimic
        la citire: API-ul de vedere micsoreaza oricum imaginile sub ~1568 de pixeli pe latura
        lunga, deci tot ce trimitem peste atat e latime de banda aruncata.
      */
      const dePus = file.type.startsWith('image/') ? (await micsoreazaPoza(file)).fisier : file
      const fd = new FormData()
      fd.append('photo', dePus)
      fd.append('month', activeMonth)
      return (await api.post('/upload/photo', fd, { timeout: 60_000 })).data
    },
    onSuccess: () => {
      toast.success('Bon încărcat')
      invalidate()
    },
    onError: (err: unknown) => toast.error(errMsg(err, 'Încărcare eșuată')),
  })

  const deleteMutation = useMutation({
    // Cu id cand bonul e in baza: poate sta in folderul altei luni (vezi `listaBonuri.ts`).
    mutationFn: async (f: UploadedFile) =>
      (
        await api.post(
          '/upload/me/delete',
          f.fileId != null ? { fileId: f.fileId } : { month: activeMonth, name: f.name },
        )
      ).data,
    onSuccess: () => {
      toast.success('Bon șters (recuperabil)')
      invalidate()
    },
    onError: (err: unknown) => toast.error(errMsg(err, 'Ștergere eșuată')),
  })

  const recoverMutation = useMutation({
    mutationFn: async (name: string) =>
      (await api.post('/upload/me/recover', { month: activeMonth, name })).data,
    onSuccess: () => {
      toast.success('Bon recuperat')
      invalidate()
    },
    onError: (err: unknown) => toast.error(errMsg(err, 'Recuperare eșuată')),
  })

  const descriereMutation = useMutation({
    mutationFn: async (v: { fileId: number; descriere: string | null }) =>
      (await api.put(`/upload/me/bon/${v.fileId}/descriere`, { descriere: v.descriere })).data,
    onSuccess: invalidate,
    onError: (err: unknown) => toast.error(errMsg(err, 'Descrierea nu s-a salvat')),
  })

  const confirmaMutation = useMutation({
    mutationFn: async () => (await api.post('/upload/me/confirma', { month: activeMonth })).data,
    onSuccess: () => {
      toast.success('Decont confirmat. Contabilitatea îl preia de aici.')
      invalidate()
    },
    onError: (err: unknown) => toast.error(errMsg(err, 'Confirmarea a eșuat')),
  })

  /*
    Evenimentele deschise, pentru legarea bonului.

    Se cer o singura data si raman in cache: lista nu se schimba cat timp omul fotografiaza.
  */
  const evenimente = useQuery<{ evenimente: Eveniment[] }>({
    queryKey: ['upload-me', 'evenimente'],
    queryFn: async () => (await api.get('/upload/me/evenimente')).data,
    staleTime: 5 * 60_000,
  })

  const evenimentMutation = useMutation({
    mutationFn: async (v: {
      fileId: number
      campanieId: number | null
      actiuneId?: number | null
    }) =>
      (
        await api.put(`/upload/me/bon/${v.fileId}/eveniment`, {
          campanieId: v.campanieId,
          actiuneId: v.actiuneId ?? null,
        })
      ).data,
    onSuccess: invalidate,
    onError: (err: unknown) => toast.error(errMsg(err, 'Evenimentul nu s-a salvat')),
  })

  const [editLine, setEditLine] = useState<RandCitit | null>(null)

  const confirmaRandMutation = useMutation({
    mutationFn: async (lineId: number) =>
      (await api.post(`/upload/me/rand/${lineId}/confirma`)).data,
    onSuccess: () => {
      toast.success('Mulțumim — confirmat.')
      invalidate()
    },
    onError: (err: unknown) => toast.error(errMsg(err, 'Confirmarea nu s-a salvat')),
  })

  const corecteazaMutation = useMutation({
    mutationFn: async (v: { lineId: number; date: Partial<RandCitit> }) =>
      (
        await api.put(`/upload/me/rand/${v.lineId}`, {
          vendorName: v.date.furnizor,
          amountTotal: v.date.total,
          docDate: v.date.data,
          articoleBon: v.date.articole,
        })
      ).data,
    onSuccess: () => {
      setEditLine(null)
      toast.success('Corectat.')
      invalidate()
    },
    onError: (err: unknown) => toast.error(errMsg(err, 'Corectura nu s-a salvat')),
  })

  const stergeRandMutation = useMutation({
    mutationFn: async (lineId: number) => (await api.delete(`/upload/me/rand/${lineId}`)).data,
    onSuccess: () => {
      setEditLine(null)
      toast.success('Rând șters.')
      invalidate()
    },
    onError: (err: unknown) => toast.error(errMsg(err, 'Ștergerea a eșuat')),
  })

  function onPick(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]
    if (file) uploadMutation.mutate(file)
    e.target.value = ''
  }

  if (query.isLoading) {
    return (
      <Card>
        <p className="text-sm text-b4l-text-muted">Se încarcă...</p>
      </Card>
    )
  }

  if (!info?.linked) {
    return (
      <Card title="Încarcă bon">
        <p className="text-sm text-b4l-text-muted">
          Contul tău nu e asociat unui angajat. Contactează administratorul ca să-ți configureze
          accesul la încărcarea bonurilor.
        </p>
      </Card>
    )
  }

  const files = info.uploaded ?? []
  const conf = info.confirmare ?? null
  /** Contabilitatea a preluat decontul — angajatul nu mai schimba nimic pe el. */
  const inchis = conf?.inchisPentruAngajat === true

  return (
    <div className="mx-auto max-w-md space-y-4">
      <Card title={`Decontul meu — ${info.employeeName}`}>
        <div className="space-y-4">
          {/*
            Link la manual CHIAR pe pagina. In meniu, „Instructiuni" sta in grupul „Resurse umane" —
            dar cine vede doar pagina asta (orice angajat fara alte drepturi) primeste „Decontul
            meu" ca intrare singura, fara grup, deci fara link la manual.
          */}
          <a
            href="/manuale/deconturi.html"
            target="_blank"
            rel="noopener noreferrer"
            className="block text-right text-sm text-b4l-blue hover:underline"
          >
            Instrucțiuni
          </a>
          <div>
            <label className="mb-1 block text-sm font-medium">Luna decontului</label>
            <select
              value={activeMonth}
              onChange={(e) => setMonth(e.target.value)}
              className="w-full rounded border border-b4l-border bg-b4l-bg px-3 py-2 text-base"
            >
              {info.months?.map((m) => (
                <option key={m} value={m}>
                  {m}
                </option>
              ))}
            </select>
          </div>

          {/*
            FARA `capture`, si deliberat.

            Cu el, butonul deschidea direct aparatul foto si nu lasa alegerea unui fisier. Or
            aplicatiile de scanare de pe telefon — Google Drive, Files — fac incadrarea si
            indreptarea perspectivei mult mai bine decat am putea noi in browser, mai ales pe
            hartie alba asezata pe o masa alba (Alexandru, 19.09.2026). Fara `capture`, telefonul
            ofera si camera, si fisierele: omul alege.

            De aceea se accepta si PDF: alea produc PDF-uri, nu poze.
          */}
          <input
            ref={fileRef}
            type="file"
            accept="image/*,application/pdf"
            onChange={onPick}
            className="hidden"
          />
          <Button
            onClick={() => fileRef.current?.click()}
            loading={uploadMutation.isPending}
            disabled={inchis}
            className="flex w-full items-center justify-center gap-2 !py-4 !text-base"
          >
            <Camera className="h-5 w-5" />
            Adaugă bon
          </Button>
          {/*
            Factura plătită pe loc, care a ajuns deja în SPV (Victor, 13.10.2026): nu se mai
            fotografiază. Omul scrie numărul, alege factura și se completează din e-factură.
          */}
          <Button
            variant="secondary"
            onClick={() => setFacturaSpv(true)}
            disabled={inchis}
            className="flex w-full items-center justify-center gap-2 !py-3 !text-base"
          >
            <FileText className="h-5 w-5" />
            Adaugă factura din SPV
          </Button>
          <p className="text-center text-xs text-b4l-text-muted">
            {inchis
              ? 'Decontul pe luna asta e deja la contabilitate — nu se mai adaugă sau șterg bonuri. Dacă ai ceva de schimbat, spune-le lor.'
              : 'Fotografiază bonul, sau alege un document scanat cu telefonul — scanarea iese mai curată. Se încarcă direct în folderul tău și se procesează automat.'}
          </p>
        </div>
      </Card>

      <Card title={`Bonuri încărcate — ${activeMonth}`}>
        {files.length > 0 ? (
          <ul className="divide-y divide-b4l-border">
            {files.map((f) => {
              const badge = statusBadge(f)
              return (
                <li
                  key={`${f.fileId ?? f.name}-${f.status}`}
                  className={`py-2.5 ${f.deleted ? 'opacity-70' : ''}`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <div className="text-base font-medium">{shortDate(f)}</div>
                      <div className="mt-0.5 flex flex-wrap items-center gap-1.5">
                        <span className={`rounded px-1.5 py-0.5 text-xs ${badge.cls}`}>
                          {badge.label}
                        </span>
                        <span className="text-xs text-b4l-text-muted">{f.sizeKb} KB</span>
                      </div>
                      {f.nota && (
                        <p className="mt-1 text-xs leading-snug text-b4l-text-muted">{f.nota}</p>
                      )}
                    </div>
                  </div>

                  {/*
                    Ce a citit sistemul din poza, aratat OMULUI care a fotografiat-o.

                    Pana acum el vedea doar „✓ Procesat · 3 randuri" si nu afla niciodata ca suma
                    citita e 233,54 in loc de 23,54 — desi el e singurul care s-a uitat la bonul
                    adevarat. Furnizorul si suma, atat: nu conturi si nu cote de TVA. Atat poate
                    verifica din memorie cineva care a platit acum trei zile.
                  */}
                  {!f.deleted && (f.citit?.length ?? 0) > 0 && (
                    <div className="mt-2 rounded bg-b4l-bg px-2 py-1.5">
                      <div className="text-xs text-b4l-text-muted">
                        {f.citit!.every((c) => c.dinSpv) ? 'Din SPV:' : 'Am citit din poză:'}
                      </div>
                      {f.citit!.map((c) => (
                        <div key={c.lineId} className="mt-1 first:mt-0.5">
                          <div className="flex flex-wrap items-baseline gap-x-1.5">
                            <span className="text-sm">{c.furnizor ?? 'furnizor necitit'}</span>
                            <span className="text-sm font-medium tabular-nums">
                              {c.total.toLocaleString('ro-RO', { minimumFractionDigits: 2 })} lei
                            </span>
                            {c.data && (
                              <span className="text-xs text-b4l-text-muted">
                                {c.data.split('-').reverse().join('.')}
                              </span>
                            )}
                            {!inchis && !c.dinSpv && (
                              <button
                                onClick={() => setEditLine(c)}
                                className="ml-auto text-xs text-b4l-blue hover:underline"
                              >
                                Editează
                              </button>
                            )}
                          </div>
                          {/*
                            Articolele de pe bon. Categoria spune „combustibil"; asta spune ce
                            s-a luat si cat — de acolo se vede daca bonul de 298 lei e o
                            alimentare sau trei cafele si o cartela.
                          */}
                          {c.articole && (
                            <div className="text-xs leading-snug text-b4l-text-muted">
                              {c.articole}
                            </div>
                          )}
                          {/*
                            Bonul are o problema concreta (suma nu se leaga, data lipseste):
                            intrebarea o primeste cel care are bonul in mana, pe loc — nu
                            contabilitatea, la sfarsitul lunii (02.11.2026).
                          */}
                          {!inchis && !c.dinSpv && (c.deVerificat?.length ?? 0) > 0 && (
                            <div
                              className={`mt-1 rounded border px-2 py-1.5 text-xs ${
                                c.confirmat
                                  ? 'border-green-200 bg-green-50 text-green-800'
                                  : 'border-amber-300 bg-amber-50 text-amber-900'
                              }`}
                            >
                              {c.confirmat ? (
                                <span>✓ Ai verificat pe bon.</span>
                              ) : (
                                <>
                                  <div className="font-medium">Uită-te pe bon: e corect?</div>
                                  <div className="leading-snug">{c.deVerificat!.join(' ')}</div>
                                  <div className="mt-1.5 flex flex-wrap gap-2">
                                    <button
                                      onClick={() => confirmaRandMutation.mutate(c.lineId)}
                                      disabled={confirmaRandMutation.isPending}
                                      className="rounded bg-green-600 px-2.5 py-1 font-medium text-white hover:bg-green-700 disabled:opacity-50"
                                    >
                                      Da, e corect
                                    </button>
                                    <button
                                      onClick={() => setEditLine(c)}
                                      className="rounded border border-amber-400 bg-white px-2.5 py-1 font-medium text-amber-900 hover:bg-amber-100"
                                    >
                                      Corectez
                                    </button>
                                  </div>
                                </>
                              )}
                            </div>
                          )}
                          {/*
                            Cotele se citeau deja de pe bon, dar nu se vedeau. La masa in oras,
                            mancarea e la 11% si bautura la 21% — despartirea spune mai mult
                            despre ce s-a consumat decat lista de paisprezece randuri.
                          */}
                          {c.cote.length > 1 && (
                            <div className="text-xs text-b4l-text-muted">
                              {c.cote.map((x, i) => (
                                <span key={i} className="mr-2 whitespace-nowrap">
                                  {x.cota}%:{' '}
                                  <span className="tabular-nums">
                                    {(x.baza + x.tva).toLocaleString('ro-RO', {
                                      minimumFractionDigits: 2,
                                    })}
                                  </span>
                                </span>
                              ))}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}

                  {/*
                    Contabilitatea cere o poza mai buna. Fara drumul asta, un bon pe care OCR-ul
                    nu-l poate citi ramane blocat: ei vad eroarea, omul vede „OCR esuat" si nu
                    stie ce sa faca.
                  */}
                  {f.refacereMotiv && (
                    <div className="mt-2 rounded border border-red-300 bg-red-50 px-2 py-1.5">
                      <div className="text-xs font-medium text-red-700">
                        Se cere o poză mai bună
                      </div>
                      <p className="text-sm text-red-800">{f.refacereMotiv}</p>
                      <p className="mt-0.5 text-xs text-b4l-text-muted">
                        Fotografiază bonul din nou, apoi șterge poza asta.
                      </p>
                    </div>
                  )}

                  {/*
                    Evenimentul de care tine bonul.

                    OPTIONAL, si asta conteaza: benzina, telefonul si consumabilele nu tin de
                    niciun eveniment. Daca ar fi obligatoriu, oamenii ar alege la intamplare doar
                    ca sa treaca mai departe, iar cifra de pe eveniment ar fi mai rea decat lipsa
                    ei.

                    Cu DATA la vedere: „Medic.ro" e si la Timisoara, si la Craiova, si unele se
                    tin periodic — fara data nu se poate alege corect.
                  */}
                  {!f.deleted && f.status !== 'mutat' && f.fileId != null && (
                    <label className="mt-2 block">
                      <span className="text-xs text-b4l-text-muted">
                        Ține de un eveniment? <span className="opacity-70">(opțional)</span>
                      </span>
                      <AlegeEveniment
                        evenimente={evenimente.data?.evenimente ?? []}
                        ales={f.campanieId ?? null}
                        aleseTitlu={f.campanieTitlu ?? null}
                        disabled={inchis || evenimentMutation.isPending}
                        onSchimba={(campanieId) =>
                          evenimentMutation.mutate({ fileId: f.fileId!, campanieId })
                        }
                      />
                    </label>
                  )}

                  {/*
                    A doua întrebare, doar când evenimentul ales CHIAR are acțiuni de teren.

                    Alexandru, 22.09.2026: „dacă atunci când fotografiază bonul, evenimentul e
                    disponibil, vreau să se poată asocia încă de atunci". Omul care a plătit
                    benzina știe acum că a fost la Ploiești; peste trei săptămâni, contabilitatea
                    vede „Petrom, 340 lei" și n-are de unde ști.

                    La majoritatea evenimentelor lista e goală și nu apare nimic: ecranul se
                    folosește de zeci de ori pe lună, iar un câmp gol cere o decizie despre nimic.
                  */}
                  {!f.deleted &&
                    f.status !== 'mutat' &&
                    f.fileId != null &&
                    (() => {
                      const ev = (evenimente.data?.evenimente ?? []).find(
                        (e) => e.id === f.campanieId,
                      )
                      const actiuni = ev?.actiuni ?? []
                      if (actiuni.length === 0) return null
                      return (
                        <label className="mt-2 block">
                          <span className="text-xs text-b4l-text-muted">
                            La care acțiune? <span className="opacity-70">(opțional)</span>
                          </span>
                          <select
                            value={f.actiuneId ?? ''}
                            disabled={inchis || evenimentMutation.isPending}
                            onChange={(e) =>
                              evenimentMutation.mutate({
                                fileId: f.fileId!,
                                campanieId: f.campanieId ?? null,
                                actiuneId: e.target.value === '' ? null : Number(e.target.value),
                              })
                            }
                            className="mt-0.5 w-full rounded border border-b4l-border bg-white px-2 py-2 text-base disabled:bg-b4l-bg"
                          >
                            <option value="">— niciuna —</option>
                            {actiuni.map((a) => (
                              <option key={a.id} value={a.id}>
                                {a.denumire}
                              </option>
                            ))}
                          </select>
                        </label>
                      )
                    })()}

                  {/*
                    Descrierea, in cuvintele angajatului.

                    OCR-ul citeste FURNIZORUL, nu MOTIVUL: „CARWASH BARENSA S.R.L." nu spune daca
                    s-a spalat masina de serviciu dupa o deplasare sau cea personala. Numai omul
                    care a platit stie, si numai acum — peste o luna nu-si mai aminteste nici el.
                  */}
                  {!f.deleted && f.status !== 'mutat' && f.fileId != null && (
                    <label className="mt-2 block">
                      <span className="text-xs text-b4l-text-muted">Ce reprezintă cheltuiala?</span>
                      <input
                        defaultValue={f.descriere ?? ''}
                        placeholder="ex. spălătorie auto, mașina de serviciu"
                        disabled={inchis}
                        onBlur={(e) => {
                          const v = e.target.value.trim()
                          if (v === (f.descriere ?? '').trim()) return
                          descriereMutation.mutate({ fileId: f.fileId!, descriere: v || null })
                        }}
                        className={`mt-0.5 w-full rounded border px-2 py-1.5 text-base disabled:bg-b4l-bg ${
                          (f.descriere ?? '').trim().length >= 3
                            ? 'border-b4l-border'
                            : 'border-amber-400 bg-amber-50'
                        }`}
                      />
                    </label>
                  )}
                  <div
                    className={`mt-2 flex flex-wrap gap-1.5 ${f.status === 'mutat' ? 'hidden' : ''}`}
                  >
                    <button
                      onClick={() => setViewFile(f)}
                      className="rounded border border-b4l-border px-2.5 py-1 text-sm hover:bg-b4l-bg"
                    >
                      Vezi
                    </button>
                    {inchis ? null : f.deleted ? (
                      <button
                        onClick={() => recoverMutation.mutate(f.name)}
                        disabled={recoverMutation.isPending}
                        className="rounded border border-green-200 bg-green-50 px-2.5 py-1 text-sm text-green-700 hover:bg-green-100 disabled:opacity-50"
                      >
                        Recuperează
                      </button>
                    ) : (
                      <button
                        onClick={() => {
                          if (
                            confirm(
                              'Ștergi bonul? Poza se mută în „șterse" și poate fi recuperată.',
                            )
                          )
                            deleteMutation.mutate(f)
                        }}
                        disabled={deleteMutation.isPending}
                        className="rounded border border-red-200 px-2.5 py-1 text-sm text-red-600 hover:bg-red-50 disabled:opacity-50"
                      >
                        Șterge
                      </button>
                    )}
                  </div>
                </li>
              )
            })}
          </ul>
        ) : (
          <p className="text-sm text-b4l-text-muted">
            Niciun bon încărcat încă pentru luna selectată.
          </p>
        )}
      </Card>

      {conf && (conf.totalBonuri > 0 || (conf.valoare?.total ?? 0) !== 0) && (
        <Card>
          {conf.valoare && (
            <div className="mb-3 border-b border-b4l-border pb-3">
              <div className="flex items-baseline justify-between gap-2">
                <span className="text-sm font-medium">Total decont</span>
                <span className="text-lg font-semibold tabular-nums">
                  {fmtSuma(conf.valoare.total, conf.valoare.moneda)}
                </span>
              </div>
              {conf.valoare.facturi !== 0 && (
                <p className="mt-0.5 text-xs text-b4l-text-muted">
                  Bonuri {fmtSuma(conf.valoare.cheltuieli, conf.valoare.moneda)} · facturi din SPV{' '}
                  {fmtSuma(conf.valoare.facturi, conf.valoare.moneda)}
                </p>
              )}
            </div>
          )}
          {conf.totalBonuri === 0 ? null : conf.confirmatLa ? (
            <div className="text-sm">
              <span className="font-medium text-green-700">✓ Decont confirmat</span>
              <p className="mt-0.5 text-xs text-b4l-text-muted">
                {new Date(conf.confirmatLa).toLocaleString('ro-RO', {
                  dateStyle: 'short',
                  timeStyle: 'short',
                })}
                {conf.confirmatDe ? ` · ${conf.confirmatDe}` : ''}. Dacă mai încarci un bon,
                confirmarea se anulează și o dai din nou.
              </p>
            </div>
          ) : (
            <>
              <div className="text-sm font-medium">
                {conf.faraDescriere.length === 0
                  ? `Toate cele ${conf.totalBonuri} bonuri au descriere.`
                  : `${conf.faraDescriere.length} din ${conf.totalBonuri} ${
                      conf.faraDescriere.length === 1 ? 'bon nu are' : 'bonuri n-au'
                    } descriere.`}
              </div>
              <p className="mt-0.5 text-xs text-b4l-text-muted">
                {conf.faraDescriere.length === 0
                  ? 'Confirmă decontul ca să-l preia contabilitatea.'
                  : 'Scrie la fiecare bon ce reprezintă cheltuiala. Fără asta nu se poate confirma.'}
              </p>
              <Button
                onClick={() => confirmaMutation.mutate()}
                loading={confirmaMutation.isPending}
                disabled={conf.faraDescriere.length > 0 || inchis}
                className="mt-2 w-full !py-3 !text-base"
              >
                Confirmă decontul
              </Button>
            </>
          )}
        </Card>
      )}

      {editLine && (
        <EditeazaRand
          rand={editLine}
          onInchide={() => setEditLine(null)}
          onSalveaza={(date) => corecteazaMutation.mutate({ lineId: editLine.lineId, date })}
          onSterge={() => {
            if (
              confirm('Ștergi rândul ăsta? Se întâmplă când același bon a fost citit de două ori.')
            )
              stergeRandMutation.mutate(editLine.lineId)
          }}
          seSalveaza={corecteazaMutation.isPending || stergeRandMutation.isPending}
        />
      )}

      {viewFile && (
        <BonViewerOverlay month={activeMonth} file={viewFile} onClose={() => setViewFile(null)} />
      )}

      {facturaSpv && (
        <AdaugaFacturaSpv
          month={activeMonth}
          onInchide={() => setFacturaSpv(false)}
          onAdaugata={() => {
            setFacturaSpv(false)
            invalidate()
          }}
        />
      )}
    </div>
  )
}

/** Overlay full-screen cu poza bonului, descarcata din Nextcloud. */
function BonViewerOverlay({
  month,
  file,
  onClose,
}: {
  month: string
  file: UploadedFile
  onClose: () => void
}) {
  const [extraRot, setExtraRot] = useState(0)
  const blobQuery = useQuery<string>({
    queryKey: ['upload-file', month, file.fileId, file.name, file.deleted],
    queryFn: async () => {
      const params = new URLSearchParams(
        file.fileId != null
          ? { fileId: String(file.fileId) }
          : { month, name: file.name, deleted: file.deleted ? '1' : '0' },
      )
      const res = await api.get(`/upload/me/file?${params.toString()}`, { responseType: 'blob' })
      return URL.createObjectURL(res.data as Blob)
    },
  })
  const rotation = extraRot % 360

  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-black/90" onClick={onClose}>
      <div className="flex items-center justify-between gap-2 px-4 py-2 text-sm text-white">
        <span className="min-w-0 truncate">{file.name}</span>
        <div className="flex shrink-0 items-center gap-2">
          {blobQuery.data && (
            <button
              onClick={(e) => {
                e.stopPropagation()
                setExtraRot((r) => (r + 90) % 360)
              }}
              className="rounded bg-white/20 px-3 py-1 hover:bg-white/30"
              title="Rotește 90°"
            >
              ↻ Rotește
            </button>
          )}
          <button onClick={onClose} className="rounded bg-white/20 px-3 py-1 hover:bg-white/30">
            ✕ Închide
          </button>
        </div>
      </div>
      {blobQuery.isLoading ? (
        <p className="py-8 text-center text-sm text-white/70">Se încarcă poza...</p>
      ) : blobQuery.isError ? (
        <p className="py-8 text-center text-sm text-red-300">Nu s-a putut încărca poza.</p>
      ) : (
        <div className="flex min-h-0 flex-1 items-center justify-center overflow-hidden p-2">
          <img
            src={blobQuery.data}
            alt={file.name}
            onClick={(e) => e.stopPropagation()}
            style={{ transform: `rotate(${rotation}deg)` }}
            className="max-h-full max-w-full object-contain transition-transform"
          />
        </div>
      )}
    </div>
  )
}

/**
 * Corectarea unui rând citit din bon, de către angajat.
 *
 * Doar ce se poate verifica de pe hârtie: furnizorul, suma, data și ce s-a cumpărat. Cotele de
 * TVA, conturile și deductibilitatea nu se citesc de pe bon — se decid, și se decid de
 * contabilitate.
 *
 * Prima variantă doar semnala („Nu e corect") și lăsa corectura altcuiva. Omul are însă bonul în
 * mână chiar atunci: un drum dus-întors pentru o cifră costă mai mult decât corectura însăși.
 */
interface FacturaSpvGasita {
  id: number
  numar: string | null
  furnizor: string | null
  cif: string | null
  data: string | null
  total: number
  folosita: boolean
}

/**
 * „Adaugă factura din SPV”: căutare după număr, nu o listă.
 *
 * Lista facturilor primite ale firmei nu e de arătat oricui are cont; cine are factura în mână îi
 * știe numărul. Se potrivește și doar sfârșitul lui — „2234” găsește „UBS2020 nr. 2234”.
 */
function AdaugaFacturaSpv({
  month,
  onInchide,
  onAdaugata,
}: {
  month: string
  onInchide: () => void
  onAdaugata: () => void
}) {
  const [numar, setNumar] = useState('')
  const [cautat, setCautat] = useState<string | null>(null)

  const cautare = useQuery<{ facturi: FacturaSpvGasita[] }>({
    queryKey: ['factura-spv', cautat, month],
    queryFn: async () =>
      (
        await api.get('/upload/me/factura-spv', {
          params: { numar: cautat, month },
        })
      ).data,
    enabled: cautat != null,
    retry: false,
  })

  const adauga = useMutation({
    mutationFn: async (anafInboundId: number) =>
      (await api.post('/upload/me/factura-spv', { month, anafInboundId })).data,
    onSuccess: () => {
      toast.success('Factura a fost adăugată pe decont')
      onAdaugata()
    },
    onError: (err: unknown) =>
      toast.error(
        (err as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error
          ?.message ?? 'Factura nu s-a putut adăuga',
      ),
  })

  const eroare = (
    cautare.error as { response?: { data?: { error?: { message?: string } } } } | null
  )?.response?.data?.error?.message
  const facturi = cautare.data?.facturi ?? []

  return (
    <div className="fixed inset-0 z-50 flex items-end bg-black/50 sm:items-center sm:justify-center">
      <div className="max-h-[90vh] w-full overflow-y-auto rounded-t-xl bg-white p-4 sm:max-w-md sm:rounded-xl">
        <h2 className="text-lg font-semibold">Adaugă factura din SPV</h2>
        <p className="mt-0.5 text-xs text-b4l-text-muted">
          Scrie numărul facturii, așa cum apare pe ea. Ajunge și doar finalul lui.
        </p>

        <form
          className="mt-3 flex gap-2"
          onSubmit={(e) => {
            e.preventDefault()
            if (numar.trim()) setCautat(numar.trim())
          }}
        >
          <input
            autoFocus
            value={numar}
            onChange={(e) => setNumar(e.target.value)}
            placeholder="ex. 2234"
            className="min-w-0 flex-1 rounded border border-b4l-border px-2 py-2 text-base"
          />
          <Button type="submit" loading={cautare.isFetching} disabled={!numar.trim()}>
            Caută
          </Button>
        </form>

        {eroare && <p className="mt-3 text-sm text-b4l-red">{eroare}</p>}
        {cautat != null && cautare.isSuccess && facturi.length === 0 && (
          <p className="mt-3 text-sm text-b4l-text-muted">
            Nicio factură cu numărul ăsta în SPV, în ultimele 6 luni. Dacă tocmai ai primit-o, poate
            n-a ajuns încă în SPV — fotografiaz-o cu „Adaugă bon”.
          </p>
        )}

        {facturi.length > 0 && (
          <ul className="mt-3 divide-y divide-b4l-border rounded border border-b4l-border">
            {facturi.map((f) => (
              <li key={f.id} className="flex items-center justify-between gap-2 px-2 py-2">
                <div className="min-w-0">
                  <div className="truncate text-sm font-medium">{f.furnizor ?? '—'}</div>
                  <div className="text-xs text-b4l-text-muted">
                    {f.numar}
                    {f.data && ` · ${f.data.split('-').reverse().join('.')}`}
                  </div>
                  <div className="text-sm tabular-nums">
                    {f.total.toLocaleString('ro-RO', { minimumFractionDigits: 2 })} lei
                  </div>
                </div>
                {f.folosita ? (
                  <span className="shrink-0 text-xs text-b4l-text-muted">deja pe un decont</span>
                ) : (
                  <Button
                    onClick={() => adauga.mutate(f.id)}
                    loading={adauga.isPending && adauga.variables === f.id}
                    disabled={adauga.isPending}
                    className="shrink-0"
                  >
                    Adaugă
                  </Button>
                )}
              </li>
            ))}
          </ul>
        )}

        <button
          onClick={onInchide}
          className="mt-4 w-full rounded border border-b4l-border px-3 py-2 text-sm"
        >
          Închide
        </button>
      </div>
    </div>
  )
}

function EditeazaRand({
  rand,
  onInchide,
  onSalveaza,
  onSterge,
  seSalveaza,
}: {
  rand: RandCitit
  onInchide: () => void
  onSalveaza: (date: Partial<RandCitit>) => void
  onSterge: () => void
  seSalveaza: boolean
}) {
  const [furnizor, setFurnizor] = useState(rand.furnizor ?? '')
  const [total, setTotal] = useState(String(rand.total ?? ''))
  const [data, setData] = useState(rand.data ?? '')
  const [articole, setArticole] = useState(rand.articole ?? '')

  const suma = Number(total.replace(',', '.'))
  const gata = Number.isFinite(suma) && suma > 0

  return (
    <div className="fixed inset-0 z-50 flex items-end bg-black/50 sm:items-center sm:justify-center">
      <div className="max-h-[90vh] w-full overflow-y-auto rounded-t-xl bg-white p-4 sm:max-w-md sm:rounded-xl">
        <h2 className="text-lg font-semibold">Corectează bonul</h2>
        <p className="mt-0.5 text-xs text-b4l-text-muted">
          Uită-te pe bon și scrie ce e scris acolo.
        </p>

        <label className="mt-3 block">
          <span className="text-xs text-b4l-text-muted">Furnizor</span>
          <input
            value={furnizor}
            onChange={(e) => setFurnizor(e.target.value)}
            className="mt-0.5 w-full rounded border border-b4l-border px-2 py-2 text-base"
          />
        </label>

        <div className="mt-3 flex gap-2">
          <label className="flex-1">
            <span className="text-xs text-b4l-text-muted">Total (lei)</span>
            <input
              inputMode="decimal"
              value={total}
              onChange={(e) => setTotal(e.target.value.replace(/[^0-9.,]/g, ''))}
              className="mt-0.5 w-full rounded border border-b4l-border px-2 py-2 text-base"
            />
          </label>
          <label className="flex-1">
            <span className="text-xs text-b4l-text-muted">Data de pe bon</span>
            <input
              type="date"
              value={data}
              onChange={(e) => setData(e.target.value)}
              className="mt-0.5 w-full rounded border border-b4l-border px-2 py-2 text-base"
            />
          </label>
        </div>

        <label className="mt-3 block">
          <span className="text-xs text-b4l-text-muted">Ce s-a cumpărat (cum scrie pe bon)</span>
          <textarea
            value={articole}
            onChange={(e) => setArticole(e.target.value)}
            rows={2}
            placeholder="ex. Benzină Standard 95 30,1604 L x 9,91"
            className="mt-0.5 w-full rounded border border-b4l-border px-2 py-2 text-base"
          />
        </label>

        <div className="mt-4 flex flex-wrap gap-2">
          <Button
            onClick={() =>
              onSalveaza({
                furnizor: furnizor.trim() || null,
                total: suma,
                data: data || null,
                articole: articole.trim() || null,
              })
            }
            disabled={!gata || seSalveaza}
            className="flex-1 !py-3"
          >
            Salvează
          </Button>
          <button
            onClick={onInchide}
            className="rounded border border-b4l-border px-3 py-2 text-sm"
          >
            Renunță
          </button>
        </div>

        {/*
          OCR-ul scoate uneori doua randuri dintr-o singura poza — bonul fiscal si chitanta de
          card, sau acelasi total citit de doua ori. Omul vede imediat ca sunt aceiasi bani.
        */}
        <button
          onClick={onSterge}
          disabled={seSalveaza}
          className="mt-3 w-full text-sm text-b4l-red hover:underline disabled:opacity-50"
        >
          Șterge rândul — e citit de două ori
        </button>
      </div>
    </div>
  )
}

/**
 * Alegerea evenimentului, cu căutare.
 *
 * Lista are câteva zeci de rânduri, iar pe telefon un `select` nativ se derulează greu și nu se
 * poate filtra (Alexandru, 19.09.2026). Cu căutare, „timi" ajunge direct la Medic.ro Timișoara.
 *
 * Se caută și în titlu, și în perioadă: „sept" aduce tot ce s-a ținut luna asta.
 */
function AlegeEveniment({
  evenimente,
  ales,
  aleseTitlu,
  disabled,
  onSchimba,
}: {
  evenimente: Eveniment[]
  ales: number | null
  aleseTitlu: string | null
  disabled?: boolean
  onSchimba: (campanieId: number | null) => void
}) {
  const [deschis, setDeschis] = useState(false)
  const [cauta, setCauta] = useState('')

  const q = cauta.trim().toLowerCase()
  const filtrate = q
    ? evenimente.filter(
        (e) => e.titlu.toLowerCase().includes(q) || e.perioada.toLowerCase().includes(q),
      )
    : evenimente

  if (!deschis) {
    return (
      <button
        type="button"
        disabled={disabled}
        onClick={() => {
          setCauta('')
          setDeschis(true)
        }}
        className="mt-0.5 w-full rounded border border-b4l-border bg-white px-2 py-2 text-left text-base disabled:bg-b4l-bg"
      >
        {aleseTitlu ?? <span className="text-b4l-text-muted">— niciunul —</span>}
      </button>
    )
  }

  return (
    <div className="mt-0.5 rounded border border-b4l-blue bg-white">
      <input
        autoFocus
        value={cauta}
        onChange={(e) => setCauta(e.target.value)}
        placeholder="caută după nume sau lună…"
        className="w-full rounded-t border-b border-b4l-border px-2 py-2 text-base"
      />
      <div className="max-h-64 overflow-y-auto">
        <button
          type="button"
          onClick={() => {
            onSchimba(null)
            setDeschis(false)
          }}
          className="block w-full border-b border-b4l-border px-2 py-2 text-left text-base text-b4l-text-muted"
        >
          — niciunul —
        </button>
        {filtrate.length === 0 ? (
          <div className="px-2 py-3 text-sm text-b4l-text-muted">Niciun eveniment potrivit.</div>
        ) : (
          filtrate.map((e) => (
            <button
              key={e.id}
              type="button"
              onClick={() => {
                onSchimba(e.id)
                setDeschis(false)
              }}
              className={`block w-full border-b border-b4l-border px-2 py-2 text-left last:border-0 ${
                e.id === ales ? 'bg-b4l-blue-light font-medium' : ''
              }`}
            >
              <span className="text-base">{e.titlu}</span>
              <span className="block text-xs text-b4l-text-muted">{e.perioada}</span>
            </button>
          ))
        )}
      </div>
      <button
        type="button"
        onClick={() => setDeschis(false)}
        className="w-full border-t border-b4l-border px-2 py-2 text-sm text-b4l-text-muted"
      >
        Închide
      </button>
    </div>
  )
}
